import React, { useRef, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress, Typography, Paper, IconButton, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { IApplicationState } from "@/store-redux";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";

interface WaveformRecorderProps {
  isRecording: boolean;
  recordingTime: number;
  audioUrl: string | null;
  isAnalyzing: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onAnalyze?: () => void;
  onReset: () => void;
  maxRecordingTime?: number;
  subtitleText?: string;
  disableAnalyze?: boolean;
}

const convertSecondToTime = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

export default function WaveformRecorder({
  isRecording,
  recordingTime,
  audioUrl,
  isAnalyzing,
  onStartRecording,
  onStopRecording,
  onAnalyze,
  onReset,
  maxRecordingTime = 120,
  subtitleText = "🎤 Đang ghi âm - Hãy đọc to câu ở dưới...",
  disableAnalyze = false,
}: WaveformRecorderProps) {
  const appMode = useSelector((state: IApplicationState) => state.appMode);
  const isDark = appMode === "dark";

  // Sound Visualizer refs
  const visualizerRef = useRef<HTMLCanvasElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const visualizerFrameRef = useRef<number | null>(null);
  const waveHistoryRef = useRef<number[]>([]);

  const stopVisualizer = useCallback(() => {
    if (visualizerFrameRef.current) {
      cancelAnimationFrame(visualizerFrameRef.current);
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    }
    streamRef.current = null;
    audioCtxRef.current = null;
    sourceRef.current = null;
    analyserRef.current = null;
    waveHistoryRef.current = [];
  }, []);

  const drawVisualizer = useCallback(() => {
    const canvas = visualizerRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    const maxSamples = 240;
    let lastSampleTime = 0;

    const draw = (timestamp: number) => {
      visualizerFrameRef.current = requestAnimationFrame(draw);

      if (timestamp - lastSampleTime > 75) {
        lastSampleTime = timestamp;

        analyser.getByteTimeDomainData(dataArray);
        let maxDev = 0;
        for (let i = 0; i < bufferLength; i++) {
          const dev = Math.abs(dataArray[i] - 128);
          if (dev > maxDev) maxDev = dev;
        }

        let amp = maxDev / 128;
        if (amp < 0.03) amp = 0;

        waveHistoryRef.current.push(amp);
        if (waveHistoryRef.current.length > maxSamples) {
          waveHistoryRef.current.shift();
        }
      }

      const width = canvas.width;
      const height = canvas.height;
      const centerY = height / 2;

      ctx.clearRect(0, 0, width, height);

      const stepWidth = width / maxSamples;
      const history = waveHistoryRef.current;

      ctx.beginPath();
      ctx.strokeStyle = isDark ? "rgba(45, 212, 191, 0.25)" : "rgba(16, 185, 129, 0.25)";
      ctx.lineWidth = 1.5;
      ctx.moveTo(0, centerY);
      ctx.lineTo(width, centerY);
      ctx.stroke();

      if (history.length === 0) return;

      for (let i = 0; i < history.length; i++) {
        const x = i * stepWidth;
        const amp = history[i];

        if (amp === 0) {
          ctx.beginPath();
          ctx.strokeStyle = "#10B981";
          ctx.lineWidth = 2;
          ctx.moveTo(x, centerY);
          ctx.lineTo(x + stepWidth, centerY);
          ctx.stroke();
        } else {
          const barHeight = Math.max(4, amp * (height * 0.85));
          const yTop = centerY - barHeight / 2;
          const yBottom = centerY + barHeight / 2;

          ctx.beginPath();
          ctx.strokeStyle = "#10B981";
          ctx.lineWidth = Math.max(1.5, stepWidth - 0.8);
          ctx.lineCap = "round";
          ctx.moveTo(x + stepWidth / 2, yTop);
          ctx.lineTo(x + stepWidth / 2, yBottom);
          ctx.stroke();
        }
      }

      const currentX = (history.length - 1) * stepWidth;
      ctx.beginPath();
      ctx.strokeStyle = "#34D399";
      ctx.lineWidth = 2.5;
      ctx.moveTo(currentX + stepWidth / 2, centerY - height * 0.42);
      ctx.lineTo(currentX + stepWidth / 2, centerY + height * 0.42);
      ctx.stroke();
    };

    visualizerFrameRef.current = requestAnimationFrame(draw);
  }, [isDark]);

  const startVisualizer = useCallback(async () => {
    try {
      waveHistoryRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContextClass();
      audioCtxRef.current = ctx;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyserRef.current = analyser;

      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;

      drawVisualizer();
    } catch (err) {
      console.error("Visualizer setup failed:", err);
    }
  }, [drawVisualizer]);

  useEffect(() => {
    if (isRecording) {
      startVisualizer();
    } else {
      stopVisualizer();
    }
    return () => stopVisualizer();
  }, [isRecording, startVisualizer, stopVisualizer]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "20px",
        bgcolor: isDark ? "rgba(30, 41, 59, 0.4)" : "#fff",
        border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(168, 85, 247, 0.15)"}`,
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0,0,0,0.02)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 800, color: "#A855F7", display: "flex", alignItems: "center", gap: 1 }}
        >
          <MicIcon fontSize="small" /> Ghi âm giọng đọc
        </Typography>

        {isRecording && (
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, color: "#EF4444", fontVariantNumeric: "tabular-nums" }}
          >
            ⏱ {convertSecondToTime(recordingTime)}
          </Typography>
        )}
      </Box>

      {!isRecording && !audioUrl && !isAnalyzing && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 1.5,
            borderRadius: "16px",
            bgcolor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
            border: `1px dashed ${isDark ? "rgba(255,255,255,0.1)" : "rgba(168,85,247,0.2)"}`,
          }}
        >
          <IconButton
            onClick={onStartRecording}
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#A855F7",
              color: "#fff",
              flexShrink: 0,
              boxShadow: "0 0 0 6px rgba(168, 85, 247, 0.2)",
              "&:hover": {
                bgcolor: "#9333EA",
                transform: "scale(1.05)",
                boxShadow: "0 0 0 10px rgba(168, 85, 247, 0.3)",
              },
              transition: "all 0.25s ease",
            }}
          >
            <MicIcon sx={{ fontSize: 30 }} />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
              Nhấn micro để bắt đầu ghi âm
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Đọc to câu phía dưới theo đúng trọng âm và ngữ điệu
            </Typography>
          </Box>
        </Box>
      )}

      {isRecording && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 2,
            p: 1.5,
            borderRadius: "16px",
            bgcolor: isDark ? "#080e1a" : "#f8fafc",
            border: "1px solid rgba(239, 68, 68, 0.3)",
          }}
        >
          <IconButton
            onClick={onStopRecording}
            sx={{
              width: 56,
              height: 56,
              bgcolor: "#EF4444",
              color: "#fff",
              flexShrink: 0,
              boxShadow: "0 0 0 6px rgba(239, 68, 68, 0.2)",
              "&:hover": { bgcolor: "#DC2626", transform: "scale(1.05)" },
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.4)" },
                "70%": { boxShadow: "0 0 0 12px rgba(239, 68, 68, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0)" },
              },
            }}
          >
            <StopIcon sx={{ fontSize: 32 }} />
          </IconButton>

          <Box sx={{ flex: 1, width: "100%", height: 50, borderRadius: "12px", overflow: "hidden" }}>
            <canvas
              ref={visualizerRef}
              width={600}
              height={50}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
          </Box>

          <Typography variant="caption" color="error" sx={{ fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
            {recordingTime > maxRecordingTime - 20
              ? `Sắp hết giờ! (${maxRecordingTime - recordingTime}s)`
              : "🔴 Đang ghi âm"}
          </Typography>
        </Box>
      )}

      {isAnalyzing && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 2 }}>
          <CircularProgress size={28} sx={{ color: "#A855F7" }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#A855F7" }}>
            Đang phân tích phát âm...
          </Typography>
        </Box>
      )}

      {!isRecording && !isAnalyzing && audioUrl && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: 1, width: "100%" }}>
            <audio controls src={audioUrl} style={{ width: "100%", height: 40, borderRadius: "8px" }} />
          </Box>
          <Stack direction="row" spacing={1.5} flexShrink={0}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={onReset}
              sx={{ borderRadius: "100px", fontWeight: 700, px: 2, textTransform: "none", height: 38 }}
            >
              Ghi âm lại
            </Button>
            {onAnalyze && (
              <Button
                variant="contained"
                size="small"
                startIcon={<SendIcon />}
                onClick={onAnalyze}
                disabled={disableAnalyze}
                sx={{
                  background: "linear-gradient(135deg, #A855F7 0%, #6366F1 100%)",
                  fontWeight: 700,
                  px: 3,
                  height: 38,
                  borderRadius: "100px",
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(168, 85, 247, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
                  },
                }}
              >
                Phân tích ngay
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
